/**
 * Suggestion Service - Real-time idea improvement suggestions
 * 
 * Combines:
 * 1. DB-based suggestions (existing valid ideas from database)
 * 2. LLM-based smart suggestions (delayed, intelligent) - NOW IN LAMBDA
 * 3. LLM suggestions considering DB results (contextual)
 */

import { lambdaClient } from '../config/http.client';
import { searchReports } from './agentcore.service';

// Re-export client for backward compatibility
const client = lambdaClient;

/**
 * Get DB-based suggestions (search for similar valid ideas)
 * These are ideas that already exist in the database, so they're proven to be valid
 */
export const getDBSuggestions = async (text) => {
  const suggestions = [];
  const trimmed = text.trim();
  
  // Need at least 5 characters for meaningful search
  if (trimmed.length < 5) {
    return suggestions;
  }
  
  try {
    // Search for similar reports in the database
    const response = await searchReports(trimmed, 'standard', null);
    
    if (response.success && response.similar_reports && response.similar_reports.length > 0) {
      // Take top 3 similar ideas
      const similarIdeas = response.similar_reports.slice(0, 3);
      
      similarIdeas.forEach((report, idx) => {
        suggestions.push({
          type: 'db',
          icon: '💡',
          text: report.title,
          reason: `${Math.round(report.similarity * 100)}% similar - from history`,
          confidence: report.similarity,
          reportId: report._id,
          priority: idx  // Order matters
        });
      });
    }
  } catch (error) {
    console.warn('[getDBSuggestions] Search failed:', error);
    // Silently fail - DB suggestions are optional
  }
  
  return suggestions;
};

/**
 * Get fast LLM-only suggestions (no DB lookup, faster response)
 * Use this for real-time suggestions while typing
 */
export const getFastLLMSuggestions = async (idea) => {
  try {
    const response = await client.post('/suggestions/llm', { 
      idea
    });
    
    // Format the suggestions with proper icon, type, and reason
    const rawSuggestions = response.data.suggestions || [];
    return rawSuggestions.map((sugg, idx) => ({
      type: 'llm',
      icon: '🚀',
      text: sugg.text,
      reason: 'AI-generated similar idea',
      confidence: 0.75 - (idx * 0.05), // Slight decrease in confidence for later suggestions
      priority: 10 + idx // Lower priority than DB suggestions
    }));
  } catch (error) {
    console.error('Fast LLM suggestions failed:', error);
    return [];
  }
};

/**
 * Get smart LLM-based suggestions with hybrid approach (LLM + DB context)
 * Can optionally include DB suggestions for context - slower but more comprehensive
 */
export const getSmartSuggestions = async (idea, dbSuggestions = []) => {
  try {
    const response = await client.post('/suggestions', { 
      idea,
      db_suggestions: dbSuggestions  // Pass DB suggestions for LLM to consider
    });
    
    // Format the suggestions with proper icon, type, and reason
    const rawSuggestions = response.data.suggestions || [];
    return rawSuggestions.map((sugg, idx) => {
      // Different formatting based on source
      if (sugg.source === 'hybrid') {
        return {
          type: 'contextual',
          icon: '🎯',
          text: sugg.text,
          reason: 'AI-generated based on existing ideas',
          confidence: 0.85 - (idx * 0.05),
          priority: 20 + idx // Lower priority than fast LLM
        };
      } else {
        return {
          type: 'smart',
          icon: '✨',
          text: sugg.text,
          reason: 'AI-generated similar idea',
          confidence: 0.80 - (idx * 0.05),
          priority: 15 + idx // Between fast LLM and hybrid
        };
      }
    });
  } catch (error) {
    console.error('Smart suggestions failed:', error);
    return [];
  }
};
